// pages/AdminProducts.tsx
import React, { useState, useEffect } from "react";
import "./AdminProducts.css";
import Sidebar from "../../components/Sidebar";
import ProductForm from "./ProductForm";
import ProductTable from "./ProductTable";
import ProductModal from "./ProductModal";
import {
  fetchProducts,
  createProduct,
  updateProduct,
  deleteProduct,
} from "../../graphql/api/productService";
import { fetchCategoriesAndSubcategories } from "../../graphql/api/categoryService";
import { Product, ProductInput, Category, Subcategory, FieldItem } from "../../interfaces/adminproduct";
import { fetchShops, fetchVendors } from "../../graphql/api/VendorApi";
import Loading from "../../components/Loading";
import AlertMessage from "../../components/AlertMessage";


const AdminProducts: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [formData, setFormData] = useState<Product>({ ...defaultFormData });
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  // const [currentTab, setCurrentTab] = useState(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [loading, setLoading] = useState(false);

  const [shops, setShops] = useState<any[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState<'success' | 'error' | 'info' | 'warning'>('info');


  const fieldTabs: FieldItem[][] = [
    [
      { name: "name", label: "Product Name", type: "text" },
      { name: "price", label: "Vendor Price", type: "number" },
      { name: "gst", label: "GST %", type: "number" },

      { name: "discountedPrice", label: "Discounted Price %", type: "number" },
      { name: "rewardFlag", label: "Reward Flag", type: "boolean" },
      { name: "marginPrice", label: "Margin Price %", type: "number" },
    ],
    [
      { name: "description", label: "Description", type: "textarea" },
      { name: "stockQuantity", label: "Stock Quantity", type: "number" },
      { name: "status", label: "Status", type: "select", options: ["Approved", "Not Approved"] }, // SELECT

    ],
  ];

  const loadData = async () => {
    setLoading(true);
    try {

      const productsData = await fetchProducts();
      const { categories, subcategories } = await fetchCategoriesAndSubcategories();
      const vendors = await fetchVendors();
      const shops = await fetchShops();

      // console.log(productsData);

      setProducts(productsData);
      setCategories(categories);
      setSubcategories(subcategories);
      setShops(shops);
      setVendors(vendors);
      setLoading(false);
    } catch (error) {
      console.error("Error loading data:", error);
      setLoading(false);
    }
  };


  useEffect(() => {

    loadData();

    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setImageFiles(files);
      setImagePreviews(files.map((file) => URL.createObjectURL(file)));
    }
  };

  const resetForm = () => {
    setFormData({ ...defaultFormData });
    setImageFiles([]);
    setImagePreviews([]);
  };

  const handleEdit = (product: Product) => {
    setFormData(product);
    setIsEditing(true);
    setImagePreviews(product.images || []);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteProduct(id);
      setProducts(products.filter((p) => p.id !== id));
      setAlertMessage('Product Delete Successfully');
      setAlertType('success');
    } catch (error) {
      console.error("Error deleting product:", error);
      setAlertMessage('Product Delete is Failed');
      setAlertType('error');
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    try {
      let updatedProduct: Product;

      const appendFormData = (fd: FormData, data: Record<string, any>) => {
        Object.entries(data).forEach(([key, value]) => {
          if (key === "id" || value == null) return;

          if (key === "variants" || key === "attributes") {
            if (Array.isArray(value) || typeof value === "object") {
              fd.append(key, JSON.stringify(value));
            }
          } else if (typeof value === "boolean" || typeof value === "number") {
            fd.append(key, value.toString());
          } else if (typeof value === "string") {
            fd.append(key, value);
          } else {
            console.warn("Skipping unsupported form field", key, value);
          }
        });
      };

      const generateSlug = (name: string) =>
        name.trim().toLowerCase().replace(/\s+/g, "-");

      if (isEditing) {
        const { id, images: existingImages, ...rest } = formData;

        if (imageFiles.length > 0) {
          // ——— multipart/form-data update ———
          const fd = new FormData();

          appendFormData(fd, rest);

          if (rest.name) {
            const slug = generateSlug(rest.name);
            fd.append("slug", slug);
          }

          imageFiles.forEach((file) => fd.append("image", file));

          for (let [k, v] of fd.entries()) {
            console.log("FD:", k, v);
          }

          updatedProduct = await updateProduct(id, fd);
        } else {
          // ——— JSON update (no new images) ———
          const preparedData: ProductInput = {
            ...rest,
            slug: (formData.name ?? "").trim().toLowerCase().replace(/\s+/g, "-"),
            attributes:
              typeof rest.attributes === "string"
                ? JSON.parse(rest.attributes)
                : rest.attributes ?? [],
            variants:
              typeof rest.variants === "string"
                ? JSON.parse(rest.variants)
                : rest.variants ?? [],
            images: existingImages ?? [],
          };

          updatedProduct = await updateProduct(id, preparedData);
        }

        setProducts((prev) =>
          prev.map((p) => (p.id === updatedProduct.id ? updatedProduct : p))
        );
      } else {
        // ——— CREATE flow ———
        const fd = new FormData();

        appendFormData(fd, formData);

        if (formData.name) {
          const slug = generateSlug(formData.name);
          fd.append("slug", slug);
        }

        imageFiles.forEach((file) => fd.append("image", file));

        for (let [k, v] of fd.entries()) {
          console.log("FD:", k, v);
        }

        const newProduct = await createProduct(fd);
        setProducts((prev) => [...prev, newProduct]);
      }

      resetForm();
      setIsEditing(false);
      setAlertMessage('Product Add/Update is successfully');
      setAlertType('success');
    } catch (err) {
      console.error("Error saving product:", err);
      setAlertMessage('Product Add/Update is Failed');
      setAlertType('error');
    }
  };

  return (
    <div className="admin-container" style={{ display: "flex" }}>
      <Sidebar isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />
      <ProductModal
        isOpen={modalIsOpen}
        onClose={() => setModalIsOpen(false)}
        product={selectedProduct}
      />
      <div
        style={{
          flex: 1,
          marginLeft: isSidebarOpen
            ? isMobile
              ? "0px"
              : "250px"
            : isMobile
              ? "0px"
              : "60px",
          transition: "margin-left 0.3s ease",
          padding: "20px",
        }}
      >
        <div className="admin-products">
          {loading && <Loading />}



          <ProductForm

            formData={formData}
            setFormData={setFormData}
            isEditing={isEditing}
            handleSubmit={handleSubmit}
            resetForm={resetForm}
            setIsEditing={setIsEditing}
            imagePreviews={imagePreviews}
            handleImageChange={handleImageChange}
            handleVideoChange={() => { }}
            categories={categories}
            // subcategories={subcategories}
            shops={shops}
            vendors={vendors}
            // currentTab={currentTab}
            // setCurrentTab={setCurrentTab}
            fieldTabs={fieldTabs}
          />
          {alertMessage && <AlertMessage message={alertMessage} type={alertType} />}

          <ProductTable
            myrole="admin"
            products={products}
            categories={categories}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            openModal={(product) => {
              setSelectedProduct(product);
              setModalIsOpen(true);
            }}
          />
        </div>


      </div>
    </div>
  );
};

const defaultFormData: Product = {
  id: "",
  name: "",
  slug: "",
  description: "",
  gst: 0,
  price: 0,
  discountedPrice: 0,
  currency: "USD",
  stockQuantity: 0,
  categoryId: "",
  vendorId: "",
  shopId: "",
  images: [],
  attributes: [],
  variants: [],
  status: "",
  rewardFlag: false,
  marginPrice: 0,
};

export default AdminProducts;
